import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import RadioGroup from "./RadioGroup";
import { School, SearchIcon, X, ChevronDown } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface SchoolSelectProps {
  label: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  id?: string;
  placeholder?: string;
  maxHeight?: string;
  disabled?: boolean;
  showAbove?: boolean;
  className?: string;
  icon?: React.ReactNode;
  labelClassName?: string;
  hideCloseIcon?: boolean;
}

const SchoolSelect: React.FC<SchoolSelectProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  required = false,
  id,
  placeholder = "Select an option",
  maxHeight = "450px",
  disabled = false,
  showAbove = true,
  className = "",
  labelClassName = "",
  hideCloseIcon = false,
  icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([
    ...options,
  ]);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const rafRef = useRef<number | null>(null);
  const selectedOption = options.find((option) => option.value === value);
  const showSearch = options.length >= 5;

  // Function to update dropdown position
  const updateDropdownPosition = () => {
    if (isOpen && selectRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const dropdownWidth = selectRect.width;
      const parsedMaxHeight = parseInt(maxHeight, 10);
      const preferredMaxHeight = Number.isFinite(parsedMaxHeight)
        ? parsedMaxHeight
        : 450;
      const viewportPadding = 8;
      const minUsableHeight = 180;
      const availableBelow = Math.max(
        0,
        window.innerHeight - Math.round(selectRect.bottom) - viewportPadding
      );
      const availableAbove = Math.max(
        0,
        Math.round(selectRect.top) - viewportPadding
      );
      const placeAbove = showAbove
        ? availableAbove > availableBelow && availableAbove >= minUsableHeight
        : availableBelow < minUsableHeight && availableAbove > availableBelow;
      const availableSpace = placeAbove ? availableAbove : availableBelow;
      const computedMaxHeight = Math.max(
        120,
        Math.min(preferredMaxHeight, availableSpace || preferredMaxHeight)
      );
      let style: React.CSSProperties = {
        position: "fixed",
        left: Math.round(selectRect.left),
        width: Math.round(dropdownWidth),
        minWidth: Math.round(dropdownWidth),
        zIndex: 9999,
        maxHeight: `${computedMaxHeight}px`,
        overflowY: "auto",
      };
      if (placeAbove) {
        style.top = Math.max(
          viewportPadding,
          Math.round(selectRect.top) - computedMaxHeight
        );
      } else {
        style.top = Math.round(selectRect.bottom);
      }
      setDropdownStyle(style);
    }
  };

  // Position dropdown when it opens and on window resize/scroll
  useLayoutEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      const scheduleUpdate = () => {
        if (rafRef.current !== null) return;
        rafRef.current = window.requestAnimationFrame(() => {
          updateDropdownPosition();
          rafRef.current = null;
        });
      };

      window.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", scheduleUpdate);

      return () => {
        window.removeEventListener("scroll", scheduleUpdate);
        window.removeEventListener("resize", scheduleUpdate);
        if (rafRef.current !== null) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [isOpen, showAbove, maxHeight]);

  // Filter options based on search term while preserving original order
  useEffect(() => {
    // Always maintain the original order from the options array
    if (searchTerm.trim() === "") {
      setFilteredOptions([...options]);
    } else {
      // Filter while preserving the original order
      // Convert search term to lowercase for case-insensitive comparison
      const searchLower = searchTerm.toLowerCase();

      const filtered = options.filter((option) => {
        // Skip the placeholder option (empty value with placeholder text)
        if (option.value === "" && option.label === placeholder) {
          return false;
        }
        // Check if the option label includes the search term (case insensitive)
        return option.label.toLowerCase().includes(searchLower);
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, placeholder]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset search and filtered options when dropdown closes
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions([...options]);
    }
  }, [isOpen, showSearch, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown when parent/page scrolls (outside-click-like behavior)
  useEffect(() => {
    if (!isOpen) return;
    const openedAt = Date.now();

    const handleScrollClose = (event: Event) => {
      if (Date.now() - openedAt < 180) {
        return;
      }
      const targetNode = event.target as Node | null;
      if (
        targetNode &&
        ((dropdownRef.current && dropdownRef.current.contains(targetNode)) ||
          (selectRef.current && selectRef.current.contains(targetNode)))
      ) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener("scroll", handleScrollClose, true);
    return () => {
      window.removeEventListener("scroll", handleScrollClose, true);
    };
  }, [isOpen]);

  return (
    <div className="w-full flex flex-col space-y-2">
      {label && (
        <label htmlFor={id} className="text-[#2D3748] text-[16px] font-medium">
          {label}
          {required && <span className="text-[#ef4444] ml-1">*</span>}
        </label>
      )}
      <div className="relative" id={id} ref={selectRef}>
        <div
          className={`w-full h-[50px] px-[20px] py-[15px] border ${
            error ? "border-[#FF3B30]" : "border-[#E2E8F0]"
          } rounded-[15px] flex items-center justify-between ${
            disabled ? "" : "cursor-pointer"
          } ${className}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span
            className={`text-[#1F1F1F] text-[14px] font-[500] truncate max-w-[calc(100%-30px)] ${
              value ? `${disabled ? "text-[#c0c0c0]" : ""}` : "text-[#c0c0c0]"
            } ${labelClassName}`}
            title={selectedOption ? selectedOption.label : placeholder}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>

          <>
            {value ? (
              <div
                className="cursor-pointer hover:bg-gray-100 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled && !hideCloseIcon) {
                    onChange("");
                  }
                }}
              >
                {hideCloseIcon ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <X className="w-4 h-4 text-gray-500" />}
              </div>
            ) : (
              <School className="w-5 h-5" />
            )}
          </>
        </div>

        {isOpen &&
          typeof window !== "undefined" &&
          ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className={`bg-white border border-gray-200 rounded-md shadow-lg scrollbar-custom ${
                showAbove ? "" : ""
              }`}
              style={dropdownStyle}
            >
              {showSearch && (
                <div className="p-2 sticky top-0 bg-white z-20">
                  <div className="relative z-20 rounded-[12px] bg-white border border-gray-200">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <SearchIcon className="w-6 h-6 text-gray-500" />
                      </div>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full pl-10 pr-3 py-2 rounded-md text-sm focus:outline-none"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
              <RadioGroup
                label=""
                options={filteredOptions}
                value={value}
                onChange={(newValue) => {
                  if (!disabled) {
                    onChange(newValue);
                    setIsOpen(false);
                  }
                }}
                id={id}
              />
              {filteredOptions.length === 0 && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No options found
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
      {error && (
        <p className="mt-0 text-[12px] font-[500] text-[#FF3B30]">{error}</p>
      )}
    </div>
  );
};

export default SchoolSelect;
